// Pure derivations for the schedule VM. Rules lifted from today's
// pages/Schedule.tsx (featured mapping, :id seeding, root-venue filter),
// components/ScheduleRoundRobin.tsx (matchday grouping and order) and
// components/ScheduleBracket.tsx (round grouping).

import type { SportPack } from '@/contexts/SportContext'
import type { DivisionRef, TournamentDetails, TournamentGame, TournamentTeam } from '@/services/api/types'
import { getImageUrl } from '@/services/api'
import { formatSeasonLabel, nameIncludesYear } from '@/utils/seasonLabel'
import { eventWhen, formatPct, teamCode, venueRef } from '../home/derive'
import type { TeamRecordVM, TeamRef } from '../home/types'
import type {
  BracketVM, CompetitionDetailVM, CompetitionFormat, CompetitionStatus, MatchdayVM, RoundRobinVM,
  ScheduleCompetitionTab, ScheduleDayVM, ScheduleGameVM, SchedulePageVM, ScheduleSideVM,
} from './types'
import { fmtDate, messages } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

export interface ScheduleFeaturedItem {
  id: number
  tournament_id: number
  display_order: number
  name: string
  year: number
  tournament_type: string
  tournament_status: string
  /** Games live or final (cancelled excluded). Absent on older payloads. */
  games_started?: number | null
  games_total?: number | null
  /** 'YYYY-MM-DD' start date or the first game's local date. */
  starts_on?: string | null
  /** #583: the competition's season and its division. Absent on older payloads. */
  season_id?: number | null
  division_id?: number | null
  /** #583: the competition's soonest upcoming (or live) game, ISO; null when none. Absent on older payloads. */
  next_game_at?: string | null
}

/** #583: a division's competitions (one division picked). Competitions of no division show under All only. */
export function filterFeaturedByDivision(items: ScheduleFeaturedItem[], divisionId: number | null): ScheduleFeaturedItem[] {
  return divisionId == null ? items : items.filter((f) => f.division_id === divisionId)
}

/** #583: a division id → its tag (null = none). Given only with 2+ live divisions. */
export type DivisionTagger = (divisionId: number | null | undefined) => DivisionRef | null

/** 'Sep 12' from a 'YYYY-MM-DD' date, read as a calendar date (no time zone shift). */
export function formatStartsOn(day: string | null | undefined): string | null {
  if (!day) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day)
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return fmtDate(d, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

const countOrNull = (v: unknown): number | null => (v === undefined || v === null || v === '' ? null : Number(v))

const formatOf = (type: string): CompetitionFormat => (type === 'round_robin' ? 'round_robin' : 'bracket')

export function deriveTabs(featured: ScheduleFeaturedItem[], tag?: DivisionTagger): ScheduleCompetitionTab[] {
  return featured.map((f) => ({
    // #583: the tab's division (the sub-line under All divisions). Only with 2+ live divisions.
    ...(tag ? { division: tag(f.division_id) } : {}),
    id: f.tournament_id,
    name: f.name,
    year: f.year,
    format: formatOf(f.tournament_type),
    status: f.tournament_status as CompetitionStatus,
    href: `/schedule/${f.tournament_id}`,
    gamesStarted: countOrNull(f.games_started),
    gamesTotal: countOrNull(f.games_total),
    startsOn: f.starts_on ?? null,
    startLabel: formatStartsOn(f.starts_on),
  }))
}

/**
 * #583: the default competition with 2+ live divisions (All, or one picked
 * division's tabs): the URL id as always, else the competition with the
 * soonest upcoming or live game (`next_game_at`), else today's rule (the
 * first featured). Below 2 live divisions the field is ignored (#418 rule).
 */
export function soonestCompetitionId(items: ScheduleFeaturedItem[], urlId: string | undefined): number | null {
  const fallback = initialCompetitionId(items.map((f) => ({ id: f.tournament_id })), urlId)
  if (urlId) return fallback
  let best: { id: number; at: number } | null = null
  for (const f of items) {
    const at = f.next_game_at ? Date.parse(f.next_game_at) : NaN
    if (Number.isFinite(at) && (!best || at < best.at)) best = { id: f.tournament_id, at }
  }
  return best ? best.id : fallback
}

/** Today's selection rule: the URL id when it's featured, else the first (#418), else null. */
export function initialCompetitionId(tabs: Array<{ id: number }>, urlId: string | undefined): number | null {
  if (urlId) {
    const idNum = parseInt(urlId, 10)
    // #418: a linked competition opens even when it isn't featured (the
    // detail fetch decides whether it exists).
    if (Number.isFinite(idNum) && idNum > 0) return idNum
  }
  return tabs.length > 0 ? tabs[0].id : null
}

/** Distinct root venues in the competition (a game's root = parent venue, else its venue), by name. */
export function rootVenues(games: TournamentGame[]): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>()
  games.forEach((g) => {
    const rootId = g.parent_venue_id ?? g.venue_id
    const rootName = g.parent_venue_name ?? g.venue_name
    if (rootId != null && rootName && !seen.has(rootId)) seen.set(rootId, rootName)
  })
  return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
}

/** A game matches when its leaf venue or its root parent equals the filter. */
export function filterGames(games: TournamentGame[], venueId: number | null): TournamentGame[] {
  if (venueId == null) return games
  return games.filter((g) => g.venue_id === venueId || g.parent_venue_id === venueId)
}

const longDate = (iso: string | null | undefined) =>
  iso ? fmtDate(iso, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : null

const started = (g: TournamentGame) => g.status === 'in_progress' || g.status === 'completed'

export function deriveDetail(d: TournamentDetails): CompetitionDetailVM {
  const t = d.tournament
  return {
    id: t.id,
    name: t.name,
    seasonId: t.season_id,
    seasonName: t.season_name ?? null,
    seasonYear: t.season_year ?? null,
    showYear: !nameIncludesYear(t.name, t.season_year),
    format: formatOf(t.type),
    type: t.type,
    status: t.status,
    note: t.public_note || null,
    startDate: t.start_date ?? null,
    endDate: t.end_date ?? null,
    startLabel: longDate(t.start_date),
    endLabel: longDate(t.end_date),
    autoAdvance: t.auto_advance !== false,
    gamesStarted: (d.games ?? []).filter(started).length,
    gamesTotal: (d.games ?? []).length,
  }
}

function side(g: TournamentGame, which: 'home' | 'away', teams: Map<number, TournamentTeam>): ScheduleSideVM {
  const teamId = which === 'home' ? g.home_team_id : g.away_team_id
  const name = (which === 'home' ? g.home_team_name : g.away_team_name) || 'TBD'
  const shortCode = (which === 'home' ? g.home_team_code : g.away_team_code) || null
  const logo = which === 'home' ? g.home_team_logo : g.away_team_logo
  const team = teamId != null ? teams.get(teamId) : undefined
  return {
    teamId,
    name,
    shortCode,
    code: teamId == null && !shortCode ? 'TBD' : teamCode(name, shortCode),
    logoUrl: getImageUrl(logo),
    color: team?.primary_color ?? null,
    href: teamId != null ? `/teams/${teamId}` : null,
    score: which === 'home' ? g.home_score : g.away_score,
    slotLabel: teamId == null ? ((which === 'home' ? g.home_slot_label : g.away_slot_label) ?? null) : null,
    seed: team?.seed ?? null,
  }
}

export function deriveGame(g: TournamentGame, teams: Map<number, TournamentTeam>): ScheduleGameVM {
  const final = g.status === 'completed'
  return {
    id: g.id,
    href: `/game/${g.id}`,
    status: g.status,
    live: g.status === 'in_progress',
    final,
    when: eventWhen(g.game_date, g.timezone),
    home: side(g, 'home', teams),
    away: side(g, 'away', teams),
    winner: final ? ((g.home_score ?? 0) > (g.away_score ?? 0) ? 'home' : (g.away_score ?? 0) > (g.home_score ?? 0) ? 'away' : null) : null,
    venue: venueRef(g),
    round: g.round_number,
    match: g.match_number,
  }
}

/** ScheduleRoundRobin's in-matchday order: time, then dated first, then venue name, then match number. */
function byMatchdayOrder(a: TournamentGame, b: TournamentGame): number {
  if (a.game_date && b.game_date) {
    const dateCompare = new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
    if (dateCompare !== 0) return dateCompare
  }
  if (a.game_date && !b.game_date) return -1
  if (!a.game_date && b.game_date) return 1
  const locCompare = (a.venue_name || '').localeCompare(b.venue_name || '')
  if (locCompare !== 0) return locCompare
  return a.match_number - b.match_number
}

const teamMap = (teams: TournamentTeam[] | undefined) => new Map((teams ?? []).map((t) => [t.team_id, t]))

export function deriveRoundRobin(games: TournamentGame[], d: TournamentDetails): RoundRobinVM {
  const teams = teamMap(d.teams)
  const groups = new Map<number, TournamentGame[]>()
  games.forEach((g) => {
    if (!groups.has(g.round_number)) groups.set(g.round_number, [])
    groups.get(g.round_number)!.push(g)
  })
  const matchdays: MatchdayVM[] = Array.from(groups.keys()).sort((a, b) => a - b).map((n) => {
    const list = [...groups.get(n)!].sort(byMatchdayOrder)
    const date = d.matchdays?.find((m) => m.matchday === n)?.matchday_date ?? null
    return {
      number: n,
      date,
      // A pure calendar DATE serialised as UTC midnight: render in UTC so the stored day shows verbatim.
      dateLabel: date ? fmtDate(date, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : null,
      games: list.map((g) => deriveGame(g, teams)),
      completed: list.filter((g) => g.status === 'completed').length,
      total: list.length,
    }
  })
  const live = games.filter((g) => g.status === 'in_progress').map((g) => deriveGame(g, teams))
  const withLive = matchdays.find((m) => m.games.some((g) => g.live))
  const unplayed = matchdays.find((m) => m.games.some((g) => g.status === 'scheduled'))
  const currentMatchday = (withLive ?? unplayed ?? matchdays[matchdays.length - 1])?.number ?? null
  return { matchdays, live, currentMatchday }
}

export function deriveBracket(games: TournamentGame[], d: TournamentDetails): BracketVM {
  const teams = teamMap(d.teams)
  const groups = new Map<number, TournamentGame[]>()
  games.forEach((g) => {
    if (!groups.has(g.round_number)) groups.set(g.round_number, [])
    groups.get(g.round_number)!.push(g)
  })
  const rounds = Array.from(groups.keys()).sort((a, b) => a - b).map((n) => ({
    number: n,
    isPlayIn: n === 0,
    games: [...groups.get(n)!].sort((a, b) => a.match_number - b.match_number).map((g) => deriveGame(g, teams)),
  }))
  const main = rounds.filter((r) => !r.isPlayIn)
  const last = main[main.length - 1]
  const final = last && last.games.length === 1 ? last.games[0] : null
  const winnerSide = final?.winner ? final[final.winner] : null
  const champion: TeamRef | null = winnerSide && winnerSide.teamId != null
    ? { id: winnerSide.teamId, name: winnerSide.name, shortCode: winnerSide.shortCode, code: winnerSide.code, logoUrl: winnerSide.logoUrl, color: winnerSide.color, href: winnerSide.href! }
    : null
  return {
    rounds,
    roundsTotal: main.length,
    teams: (d.teams ?? []).map((t) => ({
      id: t.team_id, name: t.name, shortCode: t.short_code, code: teamCode(t.name, t.short_code),
      logoUrl: getImageUrl(t.logo_url), color: t.primary_color, href: `/teams/${t.team_id}`, seed: t.seed,
    })),
    champion,
    autoAdvance: d.tournament.auto_advance !== false,
  }
}

/**
 * W-L(-D) per team from a competition's completed games (draws counted when
 * the sport allows ties), plus win share, the last five results, the current
 * streak and the home / away split. Games are taken in date order.
 */
export function competitionRecords(games: TournamentGame[], allowTie: boolean): Record<number, TeamRecordVM> {
  type Acc = { wins: number; losses: number; draws: number; seq: Array<'W' | 'L' | 'D'>; hw: number; hl: number; hd: number; aw: number; al: number; ad: number }
  const acc = new Map<number, Acc>()
  const get = (id: number) => acc.get(id) ?? (acc.set(id, { wins: 0, losses: 0, draws: 0, seq: [], hw: 0, hl: 0, hd: 0, aw: 0, al: 0, ad: 0 }), acc.get(id)!)
  const done = games
    .filter((g) => g.status === 'completed' && g.home_team_id != null && g.away_team_id != null)
    .sort((a, b) => (a.game_date && b.game_date ? new Date(a.game_date).getTime() - new Date(b.game_date).getTime() : 0) || a.round_number - b.round_number || a.match_number - b.match_number)
  for (const g of done) {
    const h = get(g.home_team_id!)
    const a = get(g.away_team_id!)
    const homeScore = g.home_score ?? 0
    const awayScore = g.away_score ?? 0
    if (homeScore > awayScore) { h.wins++; h.hw++; h.seq.push('W'); a.losses++; a.al++; a.seq.push('L') }
    else if (awayScore > homeScore) { a.wins++; a.aw++; a.seq.push('W'); h.losses++; h.hl++; h.seq.push('L') }
    else { h.draws++; h.hd++; h.seq.push('D'); a.draws++; a.ad++; a.seq.push('D') }
  }
  const ordered = [...acc.entries()].sort((x, y) => y[1].wins - x[1].wins || x[1].losses - y[1].losses)
  const split = (w: number, l: number, d: number) => (allowTie ? `${w}-${d}-${l}` : `${w}-${l}`)
  const out: Record<number, TeamRecordVM> = {}
  ordered.forEach(([id, r], i) => {
    const draws = allowTie ? r.draws : null
    const played = r.wins + r.losses + r.draws
    const last = r.seq[r.seq.length - 1]
    let run = 0
    for (let k = r.seq.length - 1; k >= 0 && r.seq[k] === last; k--) run++
    out[id] = {
      wins: r.wins,
      losses: r.losses,
      draws,
      text: draws === null ? `${r.wins}-${r.losses}` : `${r.wins}-${draws}-${r.losses}`,
      rank: i + 1,
      pct: formatPct(played ? (r.wins + r.draws / 2) / played : 0),
      last5: r.seq.slice(-5),
      streak: last ? `${last}${run}` : '',
      home: split(r.hw, r.hl, r.hd),
      away: split(r.aw, r.al, r.ad),
    }
  })
  return out
}

/** A row of GET /public/games (the season's game list). */
export interface SeasonGameRow {
  id: number
  game_date: string | null
  timezone: string | null
  venue_id: number | null
  venue_name: string | null
  parent_venue_id: number | null
  parent_venue_name: string | null
  status: string
  /** Null for a live game hidden from fans (#1280). */
  home_score: number | null
  away_score: number | null
  home_team_id: number | null
  home_team_name: string | null
  home_team_code: string | null
  home_team_logo: string | null
  away_team_id: number | null
  away_team_name: string | null
  away_team_code: string | null
  away_team_logo: string | null
  tournament_name: string | null
  /** #583: the game's season's division. Absent on older payloads. */
  division_id?: number | null
  /** #723: the season the row was listed for (a per-season list only; client-side). */
  season_id?: number
}

const dayKey = (iso: string | null, tz: string | null): string => {
  if (!iso) return 'tbd'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'tbd'
  // en-CA formats as YYYY-MM-DD
  return d.toLocaleDateString('en-CA', { timeZone: tz || 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' })
}

/**
 * #638: several active seasons' `/games` lists as one, by date (undated last),
 * then id. A game listed twice is kept once.
 */
export function mergeSeasonGameRows(lists: SeasonGameRow[][]): SeasonGameRow[] {
  const byId = new Map<number, SeasonGameRow>()
  for (const list of lists) for (const r of list) if (!byId.has(r.id)) byId.set(r.id, r)
  const time = (r: SeasonGameRow) => (r.game_date ? new Date(r.game_date).getTime() : Infinity)
  return [...byId.values()].sort((a, b) => time(a) - time(b) || a.id - b.id)
}

/**
 * Games outside any competition, grouped by event-zone day (undated last).
 * #723: under All divisions a no-division season's games (an exhibition) are
 * tagged with their season's name (`seasonTag`), so they don't read as some
 * division's regular season.
 * #652: without divisions, when the rows come from 2+ seasons (several active
 * seasons merged), every game is tagged with its season's name (`seasonName`).
 */
export function deriveSeasonGames(
  rows: SeasonGameRow[],
  tag?: DivisionTagger,
  seasonTag?: (seasonId: number) => DivisionRef | null,
  seasonName?: (seasonId: number) => DivisionRef | null,
): { days: ScheduleDayVM[]; total: number; seasonTagged: boolean } {
  const none = new Map<number, TournamentTeam>()
  const listed = rows.filter((r) => r.tournament_name == null)
  const mixed = !tag && !!seasonName && new Set(listed.map((r) => r.season_id).filter((v) => v != null)).size > 1
  const games = listed.map((r) => {
    const g = deriveGame({ ...r, round_number: 0, match_number: 0 } as unknown as TournamentGame, none)
    if (mixed) return { ...g, division: r.season_id != null ? seasonName!(r.season_id) : null }
    // #583: each game's division tag, only with 2+ live divisions.
    if (!tag) return g
    const own = tag(r.division_id) ?? (r.division_id == null && r.season_id != null && seasonTag ? seasonTag(r.season_id) : null)
    return { ...g, division: own }
  })
  const days = new Map<string, ScheduleDayVM>()
  for (const g of games) {
    const key = dayKey(g.when.startsAt, g.when.timezone)
    if (!days.has(key)) {
      days.set(key, { key, label: key === 'tbd' ? null : `${g.when.weekdayLong}, ${g.when.dateLong}`, games: [] })
    }
    days.get(key)!.games.push(g)
  }
  const ordered = [...days.values()].sort((a, b) => (a.key === 'tbd' ? 1 : b.key === 'tbd' ? -1 : a.key.localeCompare(b.key)))
  return { days: ordered, total: games.length, seasonTagged: mixed }
}

export interface ScheduleInputs {
  sport: SportPack
  featured: ScheduleFeaturedItem[]
  loadingFeatured: boolean
  featuredError: string | null
  selectedId: number | null
  details: TournamentDetails | null
  loadingDetails: boolean
  detailsError: string | null
  /** The detail fetch failed with a 404 (#418: a linked competition that doesn't exist). */
  detailsNotFound?: boolean
  venueFilter: number | null
  allVenuesLabel?: string
  /** #583: tags tabs with their division (2+ live divisions only). */
  divisionTag?: DivisionTagger
}

export type ScheduleVMData = Omit<SchedulePageVM, 'retry' | 'competitions' | 'competition' | 'venueFilter' | 'seasonGames' | 'division' | 'season'> & {
  competitions: Omit<SchedulePageVM['competitions'], 'set'>
  competition: Omit<SchedulePageVM['competition'], 'retry'>
  venueFilter: Omit<SchedulePageVM['venueFilter'], 'set'>
}

export function deriveSchedule(i: ScheduleInputs): ScheduleVMData {
  const featuredTabs = deriveTabs(i.featured, i.divisionTag)
  const d = i.details
  // #418: a competition opened by URL that isn't featured gets its own tab.
  const linked = d && i.selectedId === d.tournament.id && !featuredTabs.some((t) => t.id === d.tournament.id)
    ? [{
        id: d.tournament.id, name: d.tournament.name, year: d.tournament.season_year ?? 0, format: formatOf(d.tournament.type), status: d.tournament.status, href: `/schedule/${d.tournament.id}`,
        // Progress from the loaded games (same rule as /schedule-featured's fields).
        gamesStarted: (d.games ?? []).filter(started).length,
        gamesTotal: (d.games ?? []).filter((g) => g.status !== 'cancelled').length,
        startsOn: d.tournament.start_date ? d.tournament.start_date.slice(0, 10) : null,
        startLabel: formatStartsOn(d.tournament.start_date),
      }]
    : []
  const tabs = [...featuredTabs, ...linked]
  const games = d ? filterGames(d.games ?? [], i.venueFilter) : []
  const venues = d ? rootVenues(d.games ?? []) : []
  const format = d ? formatOf(d.tournament.type) : null
  return {
    status: i.loadingFeatured ? 'loading' : i.featuredError ? 'error' : 'ready',
    error: i.featuredError,
    sport: i.sport,
    competitions: { value: i.selectedId, options: tabs.map((t) => ({ value: t.id, label: t.name })), items: tabs },
    noCompetitions: !i.loadingFeatured && !i.featuredError && tabs.length === 0 && i.selectedId === null,
    competition: {
      status: i.loadingDetails ? 'loading' : i.detailsError ? 'error' : d ? 'ready' : 'idle',
      error: i.detailsError,
      notFound: Boolean(i.detailsError && i.detailsNotFound),
      detail: d ? deriveDetail(d) : null,
    },
    venueFilter: {
      value: i.venueFilter,
      options: [{ value: null, label: i.allVenuesLabel ?? messages(siteCoreMessages).pages.schedule.allVenues }, ...venues.map((v) => ({ value: v.id, label: v.name }))],
      visible: venues.length > 1,
    },
    format,
    roundRobin: d && format === 'round_robin' ? deriveRoundRobin(games, d) : null,
    bracket: d && format === 'bracket' ? deriveBracket(games, d) : null,
    records: d ? competitionRecords(d.games ?? [], i.sport.allowTie) : {},
    raw: { details: d, games },
  }
}

/** #652: a public season, as the picker needs it. */
export interface PickerSeason {
  id: number
  name: string | null
  year: number
  is_active?: boolean
  division_id?: number | null
}

/**
 * #652: the season picker's options: today's view (`null`) first, then every
 * public season of the sport, newest first. With one division picked, only
 * its seasons and the league-wide ones.
 */
export function seasonPickerOptions(
  seasons: PickerSeason[],
  divisionId: number | null,
  currentLabel: string | ((activeCount: number) => string),
  divisionName?: (divisionId: number) => string | null,
): Array<{ value: number | null; label: string }> {
  const listed = pickerSeasonsIn(seasons, divisionId).sort((a, b) => b.year - a.year || b.id - a.id)
  const base = listed.map((s) => formatSeasonLabel(s))
  // Divisions often share a season name ("Fall 2026" per division): add the division to tell them apart.
  const label = (s: PickerSeason, i: number) => {
    const dup = base.filter((b) => b === base[i]).length > 1
    const div = dup && s.division_id != null && divisionName ? divisionName(s.division_id) : null
    return div ? `${base[i]} · ${div}` : base[i]
  }
  const current = typeof currentLabel === 'function' ? currentLabel(listed.filter((s) => s.is_active).length) : currentLabel
  return [{ value: null, label: current }, ...listed.map((s, i) => ({ value: s.id as number | null, label: label(s, i) }))]
}

/** #652: the seasons the picker offers: with one division picked, its own and the league-wide ones. */
export function pickerSeasonsIn<S extends PickerSeason>(seasons: S[], divisionId: number | null): S[] {
  return seasons.filter((s) => divisionId == null || s.division_id == null || s.division_id === divisionId)
}

/** A row of GET /public/tournaments?seasonId= (a picked season's competitions). */
export interface SeasonTournamentRow {
  id: number
  season_id: number
  name: string
  type: string
  status: string
  start_date?: string | null
  season_year?: number | null
  division_id?: number | null
}

/**
 * #652: a picked season's competitions as schedule tabs, in start order.
 * Drafts never show (the list endpoint returns them; the detail 404s them).
 */
export function seasonTournamentsAsFeatured(rows: SeasonTournamentRow[], year: number): ScheduleFeaturedItem[] {
  return rows
    .filter((t) => t.status !== 'draft')
    .sort((a, b) => (a.start_date ?? '9999').localeCompare(b.start_date ?? '9999') || a.id - b.id)
    .map((t, i) => ({
      id: t.id,
      tournament_id: t.id,
      display_order: i,
      name: t.name,
      year: t.season_year ?? year,
      tournament_type: t.type,
      tournament_status: t.status,
      starts_on: t.start_date ? t.start_date.slice(0, 10) : null,
      season_id: t.season_id,
      division_id: t.division_id ?? null,
    }))
}
